import './FlashtalkingReport.scss';
import { useEffect, useState } from 'react';
import { PieChart } from '@Components/PieChart';
import { FullPageSpinner } from '@Components/Spinner/Spinner';
import { useAppSelector } from '../../redux';
import { currentCompanySelector } from '../../redux/company';
import { useCreativeMap } from '../../redux/creative';
import { useSetError } from '../../redux/modals';
import { fetchCometSelfInvoke, FlashtalkingLambdaFetch } from '../../utils/fetch-utils';

interface MismatchCreativeCheckCreative {
  end: string;
  ISCI: string;
  isPending?: boolean;
  start: string;
  weight: number;
}

interface MismatchedRotation {
  BPM: Record<string, MismatchCreativeCheckCreative>;
  FT: Record<string, MismatchCreativeCheckCreative>;
  name: string;
}

const formatRunDates = (start: string, end: string) => {
  const from = start.replace(/_/g, '/');
  const to = end && end !== 'null' ? end.replace(/_/g, '/') : 'Ongoing';
  return `${from} – ${to}`;
};

interface RotationSideProps {
  label: string;
  rotationMap: Record<string, MismatchCreativeCheckCreative>;
  colorMap: Record<string, string>;
}

const RotationSide = ({ label, rotationMap, colorMap }: RotationSideProps) => {
  const rotation = Object.values(rotationMap).sort((a, b) => a.weight - b.weight);

  return (
    <div className='rotation-side'>
      <div className='rotation-side-label'>{label}</div>
      {rotation.length === 0 ? (
        <div className='no-rotation'>No rotation</div>
      ) : (
        <div className='rotation-body'>
          <div className='pie'>
            <PieChart
              size={76}
              data={rotation.map(({ ISCI, weight }) => ({
                name: ISCI,
                value: weight,
                color: colorMap[ISCI]
              }))}
            />
          </div>
          <div className='isci-list'>
            {rotation.map(({ ISCI, weight, start, end, isPending }) => (
              <div key={`${ISCI}_${weight}_${start}_${end}`} className='isci-row'>
                {/* dynamic per-ISCI color looked up from the creative map */}
                <span className='swatch' style={{ backgroundColor: colorMap[ISCI] }} />
                <span className='isci'>{ISCI}</span>
                <span className='weight'>{weight}%</span>
                <span className='run-dates'>{formatRunDates(start, end)}</span>
                {isPending && <span className='pending-badge'>Pending</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

interface MismatchedPlacementProps {
  placementID: string;
  placement: MismatchedRotation;
  colorMap: Record<string, string>;
}

const MismatchedPlacement = ({ placementID, placement, colorMap }: MismatchedPlacementProps) => (
  <div className='placement-card'>
    <div className='placement-card-header'>
      <div className='placement-title'>{placement.name}</div>
      <div className='placement-meta'>Placement ID #{placementID}</div>
    </div>
    <div className='placement-compare'>
      <RotationSide label='Bliss Point' rotationMap={placement.BPM} colorMap={colorMap} />
      <RotationSide label='Flashtalking' rotationMap={placement.FT} colorMap={colorMap} />
    </div>
  </div>
);

export const FlashtalkingReport = () => {
  const company = useAppSelector(currentCompanySelector);
  const { colorMap } = useCreativeMap({ company, mediaTypes: ['audio', 'display', 'streaming'] });
  const setError = useSetError();
  const [reportInfo, setReportInfo] = useState<Record<string, MismatchedRotation>>();

  useEffect(() => {
    if (company && !reportInfo) {
      (async () => {
        try {
          const result = await fetchCometSelfInvoke<
            { company: string },
            Record<string, MismatchedRotation>
          >(FlashtalkingLambdaFetch, '/checkFTCreativeRotations', { company }, { method: 'GET' });
          setReportInfo(result);
        } catch (e) {
          setError({
            message: `Failed to load FT sync report. Error: ${e.message}`,
            reportError: e
          });
        }
      })();
    }
  }, [company, reportInfo, setError]);

  if (!reportInfo) return <FullPageSpinner />;

  const placementIDs = Object.keys(reportInfo);

  if (placementIDs.length === 0)
    return (
      <div className='ft-report'>
        <h3>Flashtalking vs BPM Sync Report</h3>
        <div className='empty-state'>Creative rotations are synchronized 🎉</div>
      </div>
    );

  return (
    <div className='ft-report'>
      <div className='report-header'>
        <h3>Flashtalking vs BPM Sync Report</h3>
        <div className='report-summary'>
          {placementIDs.length} placement{placementIDs.length === 1 ? '' : 's'} out of sync
        </div>
      </div>
      {placementIDs.map(placementID => (
        <MismatchedPlacement
          key={placementID}
          placementID={placementID}
          placement={reportInfo[placementID]}
          colorMap={colorMap}
        />
      ))}
    </div>
  );
};
